/**
 * In-flight GET dedupe and short-lived response cache for slow networks.
 * Mutations clear the cache so lists stay correct after writes.
 */

const inflight = new Map();
const responses = new Map();

/** Reference/config GETs that are safe to reuse briefly across mounts. */
const CACHEABLE_PATH =
  /\/(portals|organisations|courses|administration-levels|privileges|addons|workflows|applications|payment|branches|bank|tied-applications|plan-configurations)(\/|$|\?)/i;

const DEFAULT_TTL_MS = 45_000;

export const buildRequestCacheKey = (options = {}) => {
  const method = (options.method || 'get').toLowerCase();
  const url = options.url || '';
  const params =
    options.data && method === 'get' ? JSON.stringify(options.data) : '';
  return `${method} ${url} ${params}`;
};

export const shouldCacheGet = (options = {}) => {
  if ((options.method || 'get').toLowerCase() !== 'get') {
    return false;
  }
  if (options.cache === false || options.responseType === 'blob') {
    return false;
  }
  if (typeof options.cacheTtl === 'number') {
    return options.cacheTtl > 0;
  }
  return CACHEABLE_PATH.test(options.url || '');
};

export const getCacheTtl = (options = {}) => {
  if (typeof options.cacheTtl === 'number') {
    return options.cacheTtl;
  }
  return DEFAULT_TTL_MS;
};

export const readCachedResponse = (key) => {
  const entry = responses.get(key);
  if (!entry) {
    return null;
  }
  if (Date.now() > entry.expiresAt) {
    responses.delete(key);
    return null;
  }
  return entry.value;
};

export const writeCachedResponse = (key, value, ttlMs) => {
  if (!ttlMs || ttlMs <= 0) {
    return;
  }
  responses.set(key, { value, expiresAt: Date.now() + ttlMs });
};

export const getInflight = (key) => inflight.get(key);

export const setInflight = (key, promise) => {
  inflight.set(key, promise);
  const clear = () => {
    if (inflight.get(key) === promise) {
      inflight.delete(key);
    }
  };
  Promise.resolve(promise).then(clear, clear);
  return promise;
};

export const clearRequestCache = () => {
  responses.clear();
};

/** Drop cached GETs after a successful write so the next read is fresh. */
export const invalidateRequestCache = () => {
  responses.clear();
};
